import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useCommonTranslation } from 'c-translation';
import type {
    DashboardCalcFormat,
    DashboardCalcKind,
    DashboardCalculatedField,
} from './dashboardCalculations';
import {
    calculationError,
    DASHBOARD_CALC_FORMATS,
    DASHBOARD_CALC_FUNCTIONS,
    DASHBOARD_CALC_KINDS,
} from './dashboardCalculations';
import { humanizeField, newDashboardCalcId } from './dashboardConfig';

type Props = {
    // The calculated fields written against one source.
    calcs: DashboardCalculatedField[];
    onChange: (calcs: DashboardCalculatedField[]) => void;
    source: string;
    // That source's columns: what a formula may read, and what a dimension can be worked out per.
    fields: string[];
    // Numeric columns, offered first when inserting into a measure.
    numericFields: string[];
};

const EXAMPLES: { kind: DashboardCalcKind; key: string; expression: string }[] = [
    { kind: 'measure', key: 'ratio', expression: 'sum(a) / sum(b)' },
    { kind: 'measure', key: 'rows', expression: 'rows()' },
    { kind: 'dimension', key: 'bucket', expression: "if([Ratio] >= 1, 'Over', 'Under')" },
];

// Lists, adds and edits a source's calculated fields, checking each formula as it's typed.
const DashboardCalculatedFieldsEditor: React.FC<Props> = ({
    calcs,
    onChange,
    source,
    fields,
    numericFields,
}) => {
    const t = useCommonTranslation();
    const ns = 'Pages.DashboardHome.custom.calc';
    const update = (id: string, patch: Partial<DashboardCalculatedField>) =>
        onChange(calcs.map(c => (c.id === id ? { ...c, ...patch } : c)));
    const add = (kind: DashboardCalcKind) =>
        onChange([
            ...calcs,
            {
                id: newDashboardCalcId(),
                name: t(`${ns}.newName`, { count: calcs.length + 1 }),
                kind,
                expression: '',
                source,
                ...(kind === 'measure' ? { format: 'number' as const } : { grain: [] }),
            },
        ]);
    // Insert a column where a formula ends: aggregated for a measure, as-is for a dimension.
    const insert = (calc: DashboardCalculatedField, text: string) =>
        update(calc.id, {
            expression: `${calc.expression}${
                calc.expression && !/\s$/.test(calc.expression) ? ' ' : ''
            }${text}`,
        });
    const knownFields = fields.length ? fields : undefined;
    const sortedFields = [...fields].sort((a, b) =>
        humanizeField(a, t).localeCompare(humanizeField(b, t)),
    );

    return (
        <Stack gap={1.5}>
            {calcs.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {t(`${ns}.empty`)}
                </Typography>
            )}
            {calcs.map(calc => {
                const error = calc.expression.trim()
                    ? calculationError(calc, calcs, knownFields)
                    : null;
                // A measure can only read other measures — a dimension has no single value once
                // rows are grouped — so only those are offered to insert into one.
                const others = calcs.filter(
                    c =>
                        c.id !== calc.id &&
                        c.name.trim() &&
                        (calc.kind === 'dimension' || c.kind === 'measure'),
                );
                const insertable =
                    calc.kind === 'measure'
                        ? [
                              ...numericFields.filter(f => fields.includes(f)),
                              ...fields.filter(f => !numericFields.includes(f)),
                          ]
                        : sortedFields;
                return (
                    <Box
                        key={calc.id}
                        sx={{
                            p: 1.5,
                            border: th => `1px solid ${th.palette.divider}`,
                            borderRadius: 1,
                        }}
                    >
                        <Stack direction="row" gap={1} alignItems="center">
                            <TextField
                                size="small"
                                label={t(`${ns}.name`)}
                                value={calc.name}
                                onChange={e => update(calc.id, { name: e.target.value })}
                                sx={{ flex: 1 }}
                            />
                            <FormControl size="small" sx={{ width: 150 }}>
                                <InputLabel>{t(`${ns}.kind`)}</InputLabel>
                                <Select
                                    label={t(`${ns}.kind`)}
                                    value={calc.kind}
                                    onChange={e => {
                                        const kind = e.target.value as DashboardCalcKind;
                                        update(calc.id, {
                                            kind,
                                            ...(kind === 'measure'
                                                ? { format: calc.format ?? 'number' }
                                                : { grain: calc.grain ?? [] }),
                                        });
                                    }}
                                >
                                    {DASHBOARD_CALC_KINDS.map(kind => (
                                        <MenuItem key={kind} value={kind}>
                                            {t(`${ns}.kinds.${kind}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {calc.kind === 'measure' && (
                                <FormControl size="small" sx={{ width: 140 }}>
                                    <InputLabel>{t(`${ns}.format`)}</InputLabel>
                                    <Select
                                        label={t(`${ns}.format`)}
                                        value={calc.format ?? 'number'}
                                        onChange={e =>
                                            update(calc.id, {
                                                format: e.target.value as DashboardCalcFormat,
                                            })
                                        }
                                    >
                                        {DASHBOARD_CALC_FORMATS.map(f => (
                                            <MenuItem key={f} value={f}>
                                                {t(`${ns}.formats.${f}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <Tooltip title={t(`${ns}.delete`)}>
                                <IconButton
                                    size="small"
                                    aria-label={t(`${ns}.delete`)}
                                    onClick={() => onChange(calcs.filter(c => c.id !== calc.id))}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            label={t(`${ns}.formula`)}
                            placeholder={
                                calc.kind === 'measure'
                                    ? 'sum(total_plays) / sum(expected_plays)'
                                    : "if([Delivery] >= 1, 'Over', 'Under')"
                            }
                            value={calc.expression}
                            onChange={e => update(calc.id, { expression: e.target.value })}
                            error={error != null}
                            helperText={
                                error
                                    ? t(`${ns}.errors.${error.key}`, error.params)
                                    : t(`${ns}.help.${calc.kind}`)
                            }
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                            sx={{ mt: 1.5 }}
                        />
                        <Stack
                            direction="row"
                            gap={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ mt: 1 }}
                        >
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>{t(`${ns}.insertField`)}</InputLabel>
                                <Select
                                    label={t(`${ns}.insertField`)}
                                    value=""
                                    onChange={e => {
                                        const field = e.target.value as string;
                                        if (!field) return;
                                        insert(
                                            calc,
                                            calc.kind === 'measure' ? `sum(${field})` : field,
                                        );
                                    }}
                                >
                                    {insertable.map(f => (
                                        <MenuItem key={f} value={f}>
                                            {humanizeField(f, t)}
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                            >
                                                {f}
                                            </Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {others.map(o => (
                                <Chip
                                    key={o.id}
                                    size="small"
                                    variant="outlined"
                                    label={`[${o.name}]`}
                                    onClick={() => insert(calc, `[${o.name}]`)}
                                />
                            ))}
                        </Stack>
                        {calc.kind === 'dimension' && (
                            <Autocomplete
                                multiple
                                size="small"
                                sx={{ mt: 1.5 }}
                                options={sortedFields}
                                value={calc.grain ?? []}
                                getOptionLabel={f => humanizeField(f, t)}
                                onChange={(_, grain) => update(calc.id, { grain })}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={t(`${ns}.grain`)}
                                        helperText={t(`${ns}.grainHelp`)}
                                    />
                                )}
                            />
                        )}
                    </Box>
                );
            })}
            <Stack direction="row" gap={1}>
                <Button
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={() => add('measure')}
                >
                    {t(`${ns}.addMeasure`)}
                </Button>
                <Button
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={() => add('dimension')}
                >
                    {t(`${ns}.addDimension`)}
                </Button>
            </Stack>
            <Alert severity="info" variant="outlined" sx={{ '& code': { fontSize: 12 } }}>
                <Typography variant="body2">{t(`${ns}.reference`)}</Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    <code>{DASHBOARD_CALC_FUNCTIONS.map(f => `${f}()`).join('  ')}</code>
                    {'  '}
                    <code>+ - * / % &gt; &lt; &gt;= &lt;= = != and or not</code>
                </Typography>
                <Stack component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                    {EXAMPLES.map(ex => (
                        <Typography component="li" variant="caption" key={ex.key}>
                            <code>{ex.expression}</code> — {t(`${ns}.examples.${ex.key}`)}
                        </Typography>
                    ))}
                </Stack>
            </Alert>
        </Stack>
    );
};

export default DashboardCalculatedFieldsEditor;
